/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrBefore);

// Sur un projet de PACS, seul le nom est obligatoire : les autres champs ne
// sont contrôlés que s'ils sont saisis. Le prénom n'est jamais exigé (un
// partenaire peut ne pas en avoir) : l'assistant demande une confirmation.
const validationPartenaire = (
    data: any,
    numeroPartenaire: number,
    isProjet: boolean = false,
) => {
    const errors = {
        dateNaissance: '',
        sexe: '',
        nomFamille: '',
        identifiantNationalite: '',
        identite: '',
        doubleNom: '',
        naissanceLibelleCommuneDelegue: '',
        residenceLibelleCommuneDelegue: '',
    };

    // récupération des informations du partenaire concerné
    const {
        nomFamille = '',
        sexe = '',
        jourNaissance = '',
        moisNaissance = '',
        anneeNaissance = '',
        typeDateNaissance = null,
        premiereNationalite = '',
        identiteVerifiee = false,
        doubleNom = false,
        nomPartieDeux = '',
        adresseNaissance = {},
        adresseResidence = {},
    } = data?.conventionDeclaration?.[`partenaire${numeroPartenaire}`] || {};

    // Nom de famille
    if (_.isEmpty(nomFamille)) {
        errors.nomFamille = 'Le nom de famille est obligatoire';
    }

    // Sexe
    if (_.isEmpty(sexe) && !isProjet) {
        errors.sexe = 'Le sexe est obligatoire';
    }
    
    // Date de naissance, selon le mode de saisie : complète, mois + année
    // (jour inconnu) ou année seule. Une date partielle est contrôlée sur sa
    // période : début pour les bornes, fin pour la majorité (qui doit être
    // certaine).
    const vide = (valeur: unknown) => _.isNil(valeur) || valeur === '';
    const dateNaissanceSaisie = !vide(jourNaissance) || !vide(moisNaissance) || !vide(anneeNaissance);
    const mode = typeDateNaissance
        ?? (!vide(jourNaissance) ? 'complet' : !vide(moisNaissance) ? 'mois-annee' : 'annee');

    let debut = dayjs(null);
    let fin = dayjs(null);

    if (mode === 'mois-annee') {
        if (!vide(moisNaissance) && !vide(anneeNaissance)) {
            debut = dayjs(`${anneeNaissance}-${moisNaissance}-1`, 'YYYY-M-D');
            fin = debut.endOf('month');
        }
    } else if (mode === 'annee') {
        if (!vide(anneeNaissance)) {
            debut = dayjs(`${anneeNaissance}-1-1`, 'YYYY-M-D');
            fin = debut.endOf('year');
        }
    } else {
        debut = dayjs(
            `${anneeNaissance}-${moisNaissance}-${jourNaissance}`,
            'YYYY-M-D',
        );
        fin = debut;
    }

    const dateMajorite = dayjs().subtract(18, 'year');

    const dateNaissanceInvalide = !debut.isValid()
        || !debut.isSameOrBefore(dayjs(), 'day')
        || debut.isSameOrBefore('1900-01-01', 'day');

    // Projet : une date vide est acceptée, une date saisie reste contrôlée
    if (dateNaissanceInvalide && (dateNaissanceSaisie || !isProjet)) {
        errors.dateNaissance = 'La date de naissance est invalide';
    }

    if (debut.isValid() && fin.isAfter(dateMajorite, 'day')) {
        errors.dateNaissance = `le partenaire ${numeroPartenaire} doit être majeur`;
    }

    if (_.isEmpty(premiereNationalite) && !isProjet) {
        errors.identifiantNationalite = 'La nationalité est obligatoire';
    }

    if (identiteVerifiee !== true && !isProjet) {
        errors.identite = `Le partenaire ${numeroPartenaire} doit posséder une identité vérifiée`;
    }

    if(doubleNom && _.isEmpty(nomPartieDeux)) {
        errors.doubleNom = 'Le deuxième nom est obligatoire';
    }

    // Commune déléguée de naissance : le libellé est obligatoire dès que la case est cochée
    if (adresseNaissance?.naissanceCommuneDelegue && _.isEmpty(_.trim(adresseNaissance?.naissanceLibelleCommuneDelegue))) {
        errors.naissanceLibelleCommuneDelegue = 'Le libellé de la commune déléguée de naissance est obligatoire';
    }

    // Commune déléguée de résidence : le libellé est obligatoire dès que la case est cochée
    if (adresseResidence?.residenceCommuneDelegue && _.isEmpty(_.trim(adresseResidence?.residenceLibelleCommuneDelegue))) {
        errors.residenceLibelleCommuneDelegue = 'Le libellé de la commune déléguée de résidence est obligatoire';
    }

    return _.pickBy(errors, (value) => value !== '');
};
     
export default validationPartenaire;
     
